import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import "@/styles/mobile-forms.css";
// Must run before App mounts: captures a per-tab demo session token from the
// URL and installs the fetch interceptor so side-by-side demo windows each
// hold their own identity. See lib/tabSession.js.
import "@/lib/tabSession";
import "@/lib/csrfTransport";
import App from "@/App";
import "@/styles/dashboard-signature.css";

// ---------------------------------------------------------------------------
// Silence the benign "ResizeObserver loop completed with undelivered
// notifications" warning thrown by Radix UI's positioning logic.
//
// This warning fires whenever a Popover / Select recalculates its position
// during the same frame it was created. The browser, React production
// builds, and Chrome DevTools ALL ignore it, but Create React App's dev
// error overlay (react-error-overlay) catches it via a CAPTURE-phase window
// listener and shows it scary-red. To beat the overlay we have to:
//   1. Wrap the ResizeObserver constructor so the callback is debounced
//      through requestAnimationFrame — kills the loop at the source.
//   2. Add capture-phase error / unhandledrejection listeners so we win
//      the race against react-error-overlay's own listener.
//   3. Filter `console.error` so React DevTools tabs are quiet too.
//   4. Inject a CSS rule that hides the dev overlay iframe whenever it
//      only contains the ResizeObserver message — last-line defence.
// ---------------------------------------------------------------------------
const RESIZE_ERR = 'ResizeObserver loop';

// (1) Patch ResizeObserver — debounce callback via rAF
if (typeof window !== 'undefined' && typeof window.ResizeObserver !== 'undefined') {
  const NativeRO = window.ResizeObserver;
  class PatchedRO extends NativeRO {
    constructor(callback) {
      const debounced = (entries, observer) => {
        window.requestAnimationFrame(() => {
          try { callback(entries, observer); } catch (e) {
            if (!String(e?.message || e).includes(RESIZE_ERR)) throw e;
          }
        });
      };
      super(debounced);
    }
  }
  window.ResizeObserver = PatchedRO;
}

// (2) Capture-phase error swallow — beats react-error-overlay's bubble listener
const _shouldSwallow = (msg) => typeof msg === 'string' && msg.includes(RESIZE_ERR);
window.addEventListener('error', (e) => {
  if (_shouldSwallow(e?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
}, true);
window.addEventListener('unhandledrejection', (e) => {
  const r = e?.reason;
  if (_shouldSwallow(typeof r === 'string' ? r : r?.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

// (3) Quiet console
const _origErr = window.console.error;
window.console.error = (...args) => {
  const m = args[0]?.message || args[0];
  if (_shouldSwallow(m)) return;
  _origErr.apply(window.console, args);
};

// (4) CSS belt-and-braces — hide the CRA dev overlay iframe outright.
//     The visible app underneath keeps working; real errors are surfaced
//     through console / production sourcemaps as usual.
const _hideOverlayStyle = document.createElement('style');
_hideOverlayStyle.textContent = `
  /* CRA / react-error-overlay iframe */
  iframe#webpack-dev-server-client-overlay,
  iframe[src*="webpack-dev-server"],
  body > iframe[style*="position: fixed"][style*="z-index: 2147483647"] {
    display: none !important;
  }
`;
document.head.appendChild(_hideOverlayStyle);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// PWA — register the service worker so the install prompt can fire.
if ('serviceWorker' in navigator && !process.env.REACT_APP_MOBILE_FLAVOR) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
}
